import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge } from 'ui'

type Props = {
  _id: string
  hasTimeOff: boolean
  punchCard: MData.PunchCard | 'notLoaded' | 'noShift' | 'errorLoading' | undefined
  linkedAttendances: MData.Attendance[]
  shiftTimeOffId: string
  isOnCall: boolean
  shiftHasStarted: boolean
  permission: boolean
}

const ShiftBadges: FC<Props> = ({ _id, hasTimeOff, punchCard, linkedAttendances, shiftTimeOffId, isOnCall, permission, shiftHasStarted }) => {
  const { t } = useTranslation('legacy')

  const canSeePunchBadges = permission && (!!punchCard || shiftHasStarted)

  return (
    <div className="d-flex align-content-start flex-wrap mb-2">
      {canSeePunchBadges ? <ShiftPunchBadge punchCard={punchCard} /> : null}
      {isOnCall ? (
        <Badge className="mx-1 my-1 p-2" variant="info">
          {t('employee.mySchedule.ON_CALL')}
        </Badge>
      ) : null}
      {hasTimeOff ? (
        <a href={`/my/timeoff/${shiftTimeOffId}`}>
          <Badge className="mx-1 my-1 p-2" variant="complimentary">
            {t('employee.mySchedule.TIME_OFF')}
          </Badge>
        </a>
      ) : null}
      {linkedAttendances.map(attendance => (
        <ShiftAttendanceBadge key={`event_${_id}_attendance_${attendance._id}`} attendance={attendance} />
      ))}
    </div>
  )
}

const ShiftPunchBadge: FC<{ punchCard: MData.PunchCard | 'notLoaded' | 'noShift' | 'errorLoading' | undefined }> = ({ punchCard }) => {
  const { t } = useTranslation('legacy')

  if (typeof punchCard === 'string') return null

  const badgeInfos = {
    noPunchCard: {
      condition: !punchCard,
      variant: 'danger',
      text: t('employee.punch.STATUSES.UNPUNCHED')
    },
    approved: {
      condition: punchCard?.status === 'APPROVED',
      variant: 'success',
      text: t('employee.punch.STATUSES.APPROVED')
    },
    missingPunchedOut: {
      condition: punchCard?.flags?.notPunchedOut === true,
      variant: 'danger',
      text: t('employee.punch.STATUSES.NOT_PUNCHED_OUT')
    },
    needsApproval: {
      condition: (punchCard?.status === 'PUNCH_RULE_APPROVAL' || punchCard?.status === 'APPROVAL_REQUIRED') && !punchCard?.flags?.notPunchedOut,
      variant: 'warning',
      text: t('employee.punch.STATUSES.NEEDS_APPROVAL')
    },
    locationConflicts: {
      condition: Boolean(punchCard?.flags?.locationConflicts),
      variant: 'danger',
      text: t('employee.punch.STATUSES.LOCATION_CONFLICTS')
    }
  }

  const badgeToDisplay = Object.keys(badgeInfos).find(key => badgeInfos[key].condition)

  if (!badgeToDisplay) return null

  return (
    <Badge variant={badgeInfos[badgeToDisplay].variant} className="mx-1 my-1 p-2">
      {badgeInfos[badgeToDisplay].text}
    </Badge>
  )
}

const ShiftAttendanceBadge: FC<{ attendance: MData.Attendance }> = ({ attendance }) => {
  const { t } = useTranslation('legacy')
  const { _id, requestTypeId } = attendance

  const badgeInfos = {
    approved: {
      condition: requestTypeId === 1,
      variant: 'danger',
      text: t('employee.requestType.ABSENCE')
    },
    lateArrival: {
      condition: requestTypeId === 2,
      variant: 'warning',
      text: t('employee.requestType.LATE_ARRIVAL')
    },
    earlyOut: {
      condition: requestTypeId === 3,
      variant: 'warning',
      text: t('employee.requestType.EARLY_OUT')
    }
  }

  const badgeToDisplay = Object.keys(badgeInfos).find(key => badgeInfos[key].condition)

  if (!badgeToDisplay) return null

  return (
    <a href={`/my/attendance/${_id}`}>
      <Badge variant={badgeInfos[badgeToDisplay].variant} className="mx-1 my-1 p-2">
        {badgeInfos[badgeToDisplay].text}
      </Badge>
    </a>
  )
}

export default ShiftBadges
